import create, { UseStore } from "zustand";
import { fetchRequest, log } from "../core";

export type WorksheetConf = {
  worksheetType: string;
  workflow: string;
  features: any[]; // TODO: unknown at this time
};
export type DepartmentConf<T = { [key: string]: WorksheetConf }> = {
  departmentCode: string;
  departmentName: string;
  worksheetTypes: T;
};
export type LocationConf<T = { [key: string]: DepartmentConf }> = {
  locationCode: string;
  locationName: string;
  departments: T;
};
export type OrganizationConf<T = { [key: string]: LocationConf }> = {
  organizationCode: string;
  organizationName: string;
  locations: T;
};
export type ActivityConf<T = { [key: string]: OrganizationConf }> = {
  activity: string;
  organizations: T;
};
export type UserProfile<T = { [key: string]: ActivityConf }> = {
  principalId: string;
  email: string;
  lastName: string;
  firstName: string;
  mobilePhone: string;
  activities: T;
  userId?: string;
};

type UserProfileResult = { result: UserProfile; errors?: string[] };

const service = "usermanager";
export const getUserProfile = fetchRequest<null, UserProfileResult>({
  method: "post",
  body: () => ({ service, action: "getuserprofile" }),
});

export const getProviders = fetchRequest<null, UserProfileResult>({
  method: "post",
  body: () => ({ service, action: "getproviders" }),
});

type ActivityKey =
  | "AUDIT"
  | "AUDITING"
  | "SFEMACODINGACTIVITY"
  | "AUTOCODE"
  | "CODEREVIEW"
  | "CODING"
  | "DATAADMIN"
  | "AUDITREVIEW"
  | "USERADMIN";

export function getWorksheetTypes(
  a: UserProfile,
  activity: ActivityKey | string,
  organization: string,
  locations: string[]
): string[] {
  if (
    !a?.activities ||
    !a.activities[activity]?.organizations ||
    !a.activities[activity].organizations[organization]?.locations
  ) {
    return [];
  }
  const loc = a.activities[activity].organizations[organization].locations;
  const types = {};

  locations.forEach((k) => {
    const l = loc[k];
    const d = l?.departments;

    if (d) {
      Object.values(d).forEach((v) => {
        const w = v.worksheetTypes;
        if (w) {
          Object.values(w).forEach((t) => {
            if (t.worksheetType) {
              types[t.worksheetType] = t.worksheetType;
            }
          });
        }
      });
    }
  });
  return Object.values(types);
}

export function getActivites(a: UserProfile): ActivityConf[] {
  if (!a?.activities) {
    return [];
  }
  return Object.values(a.activities);
}

export interface NameCode {
  name: string;
  code?: string;
}

export type DepartmentList = Omit<
  DepartmentConf,
  "worksheetTypes" | "departmentName" | "departmentCode"
> &
  NameCode & {
    worksheetTypes: WorksheetConf[];
  };
export type LocationList = Omit<
  LocationConf,
  "departments" | "locationName" | "locationCode"
> &
  NameCode & {
    departments: DepartmentList[];
  };
export type OrganizationList = Omit<
  OrganizationConf,
  "locations" | "organizationName" | "organizationCode"
> &
  NameCode & {
    locations: LocationList[];
  };
export type ActivityList = Omit<ActivityConf, "organizations"> & {
  organizations: OrganizationList[];
};
export type UserProfileLists = Omit<UserProfile, "activities"> & {
  activities: ActivityList[];
};

function parseUserProfile(profile: UserProfile): UserProfileLists {
  if (!profile?.activities) {
    throw "No user activities";
  }
  const activities = Object.values(profile.activities).map((a) => {
    const organizations = Object.values(a?.organizations || {}).map((b) => {
      const locations = Object.values(b?.locations || {}).map((c) => {
        const departments = Object.values(c?.departments || {}).map((d) => {
          d.departmentName = d.departmentName || "NULL - CHECK CONFIGURATION";
          const worksheetTypes = Object.values(d?.worksheetTypes || {}).map(
            (e) => e
          );
          return {
            worksheetTypes,
            name: d.departmentName,
            code: d.departmentCode,
          };
        });
        return {
          departments,
          name: c.locationName,
          code: c.locationCode,
        };
      });

      const o = {
        locations,
        name: b.organizationName,
        code: b.organizationCode,
      };
      return o;
    });
    return { ...a, organizations };
  });

  return { ...profile, activities };
}

type UserProfileData = {
  user: UserProfile;
  profile: UserProfileLists;
  worksheetTypes: string[];
  activities: ActivityConf[];
  error: string;
};

export type UserProfileApi = {
  data: UseStore<UserProfileData>;
  load: () => Promise<UserProfileResult>;
  activities: () => string[];
  activity: (name: ActivityKey) => ActivityList;
  organizationsByActivity: (activity: string) => OrganizationList[];
  worksheetTypesByActivity: (
    name: ActivityKey,
    organization: string,
    locations: string[]
  ) => string[];
};

type UserProfileApiConf = {
  profileProvider?: () => Promise<UserProfileResult>;
};

export function createUserProfileApi({
  profileProvider,
}: UserProfileApiConf): UserProfileApi {
  const fetch = profileProvider || getUserProfile;

  const data = create(() => ({
    user: null,
    profile: null,
    worksheetTypes: null,
    activities: null,
    error: null,
  }));

  function getError(err: string[]): string | null {
    if (!err?.length) {
      return null;
    }
    return (err || ["Error loading user profile"]).join(", ");
  }

  async function load() {
    data.setState({ error: null });

    const response = await fetch();
    const { result, errors } = response || {};
    const error = getError(
      errors || result ? null : ["No user profile returned"]
    );

    if (error?.length) {
      data.setState({ error: null });
      log.error(error);
      return Promise.reject(error);
    } else {
      const profile = parseUserProfile(result);
      if (!result.userId) {
        result.userId = result.principalId;
      }
      data.setState({ profile, user: result });

      return response;
    }
  }

  const activity: (name: string) => ActivityList = (a = "CODING") => {
    const p = data.getState().profile;

    if (!p) {
      // TODO: log profile problems in data dog
      return null;
    }

    return p.activities.find((v: ActivityList) => v?.activity === a);
  };

  function activities() {
    const p = data.getState().profile;
    if (!p.activities) {
      return [];
    }
    return p.activities.map((a: ActivityList) => a.activity);
  }

  function organizationsByActivity(name: string): OrganizationList[] {
    const a = activity(name);
    if (!a?.organizations?.length) {
      return [];
    }
    return a.organizations;
  }

  function worksheetTypesByActivity(
    name: ActivityKey,
    organization: string,
    locations: string[]
  ) {
    const a = activity(name);
    if (!a?.organizations?.length || !locations?.length) {
      return [];
    }

    const b = a.organizations.find((v) => v.name === organization);
    if (!b?.locations?.length) {
      return [];
    }

    const list = b.locations.filter((v) => locations.includes(v.name));
    const map = {};

    list.forEach((c) => {
      c.departments.forEach((d) => {
        d.worksheetTypes.forEach((e) => {
          map[e.worksheetType] = e.worksheetType;
        });
      });
    });

    return Object.values(map) as string[];
  }

  return {
    data,
    load,
    activity,
    activities,
    organizationsByActivity,
    worksheetTypesByActivity,
  };
}

export const userProfileApi = createUserProfileApi({});
